import React, { useState, useEffect, useCallback } from 'react';
import {
  Calendar,
  RefreshCw,
  AlertTriangle,
  Clock,
  XCircle,
  Zap,
  ArrowRight,
  Shield,
  Users,
  Hash,
  User
} from 'lucide-react';
import api from '../../services/api';

const SMSStatusManager = () => {
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const [counts, setCounts] = useState([]);
  const [userCounts, setUserCounts] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedUser, setSelectedUser] = useState('');
  const [allUsers, setAllUsers] = useState([]); // Store all users with counts

  // Bulk update state
  const [fromStatus, setFromStatus] = useState('');
  const [toStatus, setToStatus] = useState('FAILED');
  const [updating, setUpdating] = useState(false);
  const [updateResult, setUpdateResult] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const fetchCounts = useCallback(async () => {
    setLoading(true);
    setUpdateResult(null);
    try {
      // Always fetch all users and counts for the date
      const response = await api.get(`/admin/sms-status/counts?date=${date}`);
      if (response.data.success) {
        const allCounts = response.data.counts || [];
        const allUserData = response.data.users || [];
        
        // Calculate per-user totals
        const userTotals = {};
        allUserData.forEach(item => {
          if (!userTotals[item.user_id]) {
            userTotals[item.user_id] = {
              user_id: item.user_id,
              username: item.username,
              total: 0,
              statuses: {}
            };
          }
          userTotals[item.user_id].statuses[item.status] = item.count;
          userTotals[item.user_id].total += item.count;
        });
        
        setAllUsers(Object.values(userTotals));
        
        // If a user is selected, filter the counts
        if (selectedUser) {
          const filteredUserData = allUserData.filter(u => String(u.user_id) === String(selectedUser));
          const statusCounts = {};
          let total = 0;
          
          filteredUserData.forEach(item => {
            statusCounts[item.status] = (statusCounts[item.status] || 0) + item.count;
            total += item.count;
          });
          
          const countsArray = Object.entries(statusCounts).map(([status, count]) => ({
            status,
            count
          }));
          
          setCounts(countsArray);
          setUserCounts(filteredUserData);
          setTotalCount(total);
        } else {
          setCounts(allCounts);
          setUserCounts(allUserData);
          setTotalCount(response.data.total || 0);
        }
      }
    } catch (error) {
      console.error('Failed to fetch SMS status counts:', error);
      setCounts([]);
      setUserCounts([]);
      setAllUsers([]);
      setTotalCount(0);
    }
    setLoading(false);
  }, [date, selectedUser]);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  const handleBulkUpdate = async () => {
    if (!fromStatus || !toStatus) return;
    setUpdating(true);
    setUpdateResult(null);
    try {
      const response = await api.post('/admin/sms-status/bulk-update', {
        date,
        from_status: fromStatus,
        to_status: toStatus,
        user_id: selectedUser || undefined,
      });
      if (response.data.success) {
        setUpdateResult({ type: 'success', message: response.data.message, updated: response.data.updated });
        fetchCounts();
      } else {
        setUpdateResult({ type: 'error', message: response.data.message });
      }
    } catch (error) {
      setUpdateResult({ type: 'error', message: error.response?.data?.message || 'Failed to update SMS records' });
    }
    setUpdating(false);
    setConfirmOpen(false);
    setFromStatus('');
  };

  const getStatusIcon = (status) => {
    switch (status?.toUpperCase()) {
      case 'PENDING': return <Clock size={18} className="text-yellow-500" />;
      case 'BURST': return <Zap size={18} className="text-orange-500" />;
      case 'FAILED': return <XCircle size={18} className="text-red-500" />;
      case 'SUBMITTED': return <ArrowRight size={18} className="text-blue-500" />;
      default: return <AlertTriangle size={18} className="text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case 'PENDING': return 'bg-yellow-50 border-yellow-200 text-yellow-800';
      case 'BURST': return 'bg-orange-50 border-orange-200 text-orange-800';
      case 'FAILED': return 'bg-red-50 border-red-200 text-red-800';
      case 'SUBMITTED': return 'bg-blue-50 border-blue-200 text-blue-800';
      default: return 'bg-gray-50 border-gray-200 text-gray-800';
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status?.toUpperCase()) {
      case 'PENDING': return 'bg-yellow-100 text-yellow-800';
      case 'BURST': return 'bg-orange-100 text-orange-800';
      case 'FAILED': return 'bg-red-100 text-red-800';
      case 'SUBMITTED': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Group user counts by user
  const groupedUsers = userCounts.reduce((acc, item) => {
    if (!acc[item.user_id]) {
      acc[item.user_id] = { user_id: item.user_id, username: item.username, statuses: {} };
    }
    acc[item.user_id].statuses[item.status] = item.count;
    return acc;
  }, {});

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Shield size={28} className="text-indigo-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">SMS Status Manager</h1>
            <p className="text-sm text-gray-500">View and manage SMS statuses by date (excludes DELIVERED)</p>
          </div>
        </div>
        <button
          onClick={fetchCounts}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Date and User Selector */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex items-center gap-4 flex-wrap">
          <Calendar size={20} className="text-gray-500" />
          <label className="text-sm font-medium text-gray-700">Select Date:</label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
          />
          
          <div className="flex items-center gap-2 ml-4">
            <User size={20} className="text-gray-500" />
            <label className="text-sm font-medium text-gray-700">Filter by User:</label>
            <select
              value={selectedUser}
              onChange={(e) => { setSelectedUser(String(e.target.value)); setFromStatus(''); setConfirmOpen(false); }}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm min-w-[250px]"
            >
              <option value="">All Users</option>
              {allUsers
                .filter(user => user.total > 0)
                .sort((a, b) => b.total - a.total)
                .map(user => (
                <option key={user.user_id} value={String(user.user_id)}>
                  {user.username} (ID: {user.user_id}) - {user.total.toLocaleString()} SMS
                </option>
              ))}
            </select>
          </div>
          
          <span className="text-sm text-gray-500 ml-auto">
            Total: <span className="font-semibold text-gray-900">{totalCount.toLocaleString()}</span> SMS 
            {selectedUser && <span> for <span className="font-medium">{allUsers.find(u => String(u.user_id) === String(selectedUser))?.username}</span></span>}
            {' '} (excluding DELIVERED)
          </span>
        </div>
      </div>

      {/* Status Count Cards */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw size={24} className="animate-spin text-indigo-600" />
          <span className="ml-2 text-gray-500">Loading...</span>
        </div>
      ) : counts.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center mb-6">
          <AlertTriangle size={48} className="mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500 text-lg">No non-delivered SMS found for {date}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {counts.map((item) => (
            <div
              key={item.status}
              className={`rounded-xl border-2 p-5 cursor-pointer transition-all hover:shadow-md ${getStatusColor(item.status)} ${fromStatus === item.status ? 'ring-2 ring-indigo-500 shadow-lg' : ''}`}
              onClick={() => {
                if (item.status !== 'FAILED') {
                  setFromStatus(item.status);
                  setConfirmOpen(false);
                  setUpdateResult(null);
                }
              }}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  {getStatusIcon(item.status)}
                  <span className="font-semibold text-sm uppercase">{item.status}</span>
                </div>
                {item.status !== 'FAILED' && (
                  <span className="text-xs text-gray-400">Click to select</span>
                )}
              </div>
              <div className="text-3xl font-bold">{item.count.toLocaleString()}</div>
              <div className="text-xs mt-1 opacity-70">
                {totalCount > 0 ? ((item.count / totalCount) * 100).toFixed(1) : 0}% of total
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Bulk Update Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Zap size={20} className="text-indigo-600" />
          Bulk Status Update
        </h2>

        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">From Status</label>
            <select
              value={fromStatus}
              onChange={(e) => { setFromStatus(e.target.value); setConfirmOpen(false); setUpdateResult(null); }}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-sm min-w-[160px]"
            >
              <option value="">-- Select --</option>
              {counts.filter(c => c.status !== 'FAILED').map(c => (
                <option key={c.status} value={c.status}>
                  {c.status} ({c.count.toLocaleString()})
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center pb-2">
            <ArrowRight size={20} className="text-gray-400" />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">To Status</label>
            <select
              value={toStatus}
              onChange={(e) => { setToStatus(e.target.value); setConfirmOpen(false); }}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-sm min-w-[160px]"
            >
              <option value="FAILED">FAILED</option>
            </select>
          </div>

          <div>
            {!confirmOpen ? (
              <button
                onClick={() => setConfirmOpen(true)}
                disabled={!fromStatus || !toStatus || fromStatus === toStatus || updating}
                className="px-5 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium text-sm"
              >
                Update Status
              </button>
            ) : (
              <div className="flex items-center gap-2">
                <button
                  onClick={handleBulkUpdate}
                  disabled={updating}
                  className="px-5 py-2 bg-red-700 text-white rounded-lg hover:bg-red-800 disabled:opacity-50 transition-colors font-medium text-sm flex items-center gap-2"
                >
                  {updating ? <RefreshCw size={14} className="animate-spin" /> : <AlertTriangle size={14} />}
                  {updating ? 'Updating...' : 'Confirm Update'}
                </button>
                <button
                  onClick={() => setConfirmOpen(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>

        {fromStatus && (
          <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
            <AlertTriangle size={14} className="inline mr-1" />
            This will change <strong>{counts.find(c => c.status === fromStatus)?.count.toLocaleString() || 0}</strong> SMS
            from <strong>{fromStatus}</strong> to <strong>{toStatus}</strong> for date <strong>{date}</strong>
            {selectedUser && (
              <> for user <strong>{allUsers.find(u => String(u.user_id) === String(selectedUser))?.username}</strong></>
            )}.
            {toStatus === 'FAILED' && ' Worker will NOT retry these messages.'}
          </div>
        )}

        {updateResult && (
          <div className={`mt-3 p-3 rounded-lg text-sm ${updateResult.type === 'success' ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'}`}>
            {updateResult.type === 'success' ? '✓' : '✗'} {updateResult.message}
          </div>
        )}
      </div>

      {/* Per-User Breakdown */}
      {!selectedUser && Object.keys(groupedUsers).length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Users size={20} className="text-indigo-600" />
            Per-User Breakdown
          </h2>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-semibold text-gray-600">
                    <div className="flex items-center gap-1"><Hash size={14} /> User ID</div>
                  </th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-600">
                    <div className="flex items-center gap-1"><Users size={14} /> Username</div>
                  </th>
                  {counts.map(c => (
                    <th key={c.status} className="text-right py-3 px-4 font-semibold text-gray-600">
                      <div className="flex items-center justify-end gap-1">
                        {getStatusIcon(c.status)}
                        {c.status}
                      </div>
                    </th>
                  ))}
                  <th className="text-right py-3 px-4 font-semibold text-gray-600">Total</th>
                </tr>
              </thead>
              <tbody>
                {Object.values(groupedUsers).map((user) => {
                  const userTotal = Object.values(user.statuses).reduce((a, b) => a + b, 0);
                  return (
                    <tr key={user.user_id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4 font-mono text-gray-600">{user.user_id}</td>
                      <td className="py-3 px-4 font-medium text-gray-900">{user.username}</td>
                      {counts.map(c => (
                        <td key={c.status} className="py-3 px-4 text-right">
                          {user.statuses[c.status] ? (
                            <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(c.status)}`}>
                              {user.statuses[c.status].toLocaleString()}
                            </span>
                          ) : (
                            <span className="text-gray-300">—</span>
                          )}
                        </td>
                      ))}
                      <td className="py-3 px-4 text-right font-semibold text-gray-900">{userTotal.toLocaleString()}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default SMSStatusManager;
